import classnames from 'classnames';
import type React from 'react';
import type { SelectableItem } from '../../../../../../models/CampaignModel';
import { ProductType } from '../../../../../../models/product/ProductType';
import { formatList } from '../../../../../../utils/intl';
import useModal from '../../../../../modal/Modal';
import { Button, DefaultBlockContent } from '../../../../../shared';
import ProductCardHat from './ProductCardHat';
import ProductCardImage from './ProductCardImage';
import ProductPrice from './ProductPrice';
import { SelectedIcon } from '../components/selection-icon/SelectedIcon';
import ProductModal from '~/components/modal/modals/ProductModal/ProductModal';

interface Props {
  product: SelectableItem;
  toggleProductSelection: (product: SelectableItem) => void;
  shouldFocus: boolean;
  isSelected: boolean;
  isOpen: boolean;
}

const ProductCardFace: React.FC<Props> = ({ shouldFocus, product, toggleProductSelection, isSelected, isOpen }) => {
  const {
    displayData: { name, description, shortDescription, backgroundImageUrl, infoTag, optionalPriceTitle },
    productType,
    productKey,
  } = product;
  const cardClass = classnames('card-face', productType, { 'is-selected': isSelected, 'with-background': !!backgroundImageUrl });

  const { open } = useModal();

  const aria = {
    'aria-label': isSelected ? `${name} lagt til i handlekurv, trykk igjen for å angre` : `Legg til ${name} i handlekurv`,
    'aria-expanded': productType === ProductType.Receiver || productType === ProductType.Equipment ? isOpen : undefined,
  };

  const cardHeaderClass =
    product.productType === ProductType.Installation || product.productType === ProductType.Equipment ? 'card-header-flex' : '';

  const conflictingProductNames = product.displayData.conflictingProducts?.map(({ name }) => name) ?? [];
  const hasConflictingProducts = conflictingProductNames.length > 0;

  return (
    <div className={cardClass} data-testid={productKey ?? name.toLowerCase()}>
      <ProductCardHat product={product} />

      {isSelected && <SelectedIcon />}

      <div className="card-content">
        <div className={cardHeaderClass}>
          <ProductCardImage product={product} />

          <strong className="title-4 card-name" aria-label={isSelected ? `Valgt ${name}` : name}>
            {name}
          </strong>
        </div>

        {shortDescription && (
          <div className="sub-body">
            <DefaultBlockContent blocks={shortDescription} />
          </div>
        )}

        {infoTag && <p className="metadata card-infotag-text">{infoTag}</p>}
        {hasConflictingProducts && (
          <p className="metadata card-infotag-text">Kan ikke kombineres med {formatList(conflictingProductNames, 'disjunction')}</p>
        )}

        <ProductPrice pricing={product.pricing} receiver={productType === ProductType.Receiver} optionalPriceTitle={optionalPriceTitle} />
      </div>

      <div className="card-footer">
        {description && (
          <Button
            type="tertiary"
            onClick={() => open(<ProductModal product={product} toggleProductSelection={toggleProductSelection} isSelected={isSelected} />)}
            aria-label={`Les mer om ${name}`}
            data-track-id="card-button"
            data-track-card-name={name}
          >
            Les mer
          </Button>
        )}
        <Button
          type="secondary"
          data-testid={isSelected ? `remove ${name.toLowerCase()}` : `add ${name.toLowerCase()}`}
          focusOnRender={shouldFocus}
          onClick={() => toggleProductSelection(product)}
          {...aria}
        >
          {isSelected ? 'Angre' : 'Legg til'}
        </Button>
      </div>
    </div>
  );
};

export default ProductCardFace;
